import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { EntityAvatar } from '@/components/ui/entity-avatar'
import { toastFailure } from '@/lib/feedback'
import { CategoryChip } from '../components/CategoryChip'
import { upcomingLists } from './api'
import { dayOfMonth, monthOf, weekdayOf } from './listDates'
import type { ListRow } from './types'

/**
 * "Komende events en mailings" (design §4.7): up to four tiles for the lists that start in the next
 * 60 days or are running now. Without such lists the strip does not exist.
 */
export function UpcomingListTiles() {
  const [rows, setRows] = useState<ListRow[]>([])
  useEffect(() => {
    let cancelled = false
    upcomingLists().then(
      (found) => {
        if (!cancelled) setRows(found)
      },
      (caught: unknown) => {
        if (!cancelled) toastFailure(caught, 'Komende lijsten laden mislukt.')
      },
    )
    return () => {
      cancelled = true
    }
  }, [])

  if (rows.length === 0) return null
  return (
    <section aria-label="Komende events en mailings" className="flex flex-col gap-2">
      <h2 className="text-sm font-semibold">Komende events en mailings</h2>
      <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {rows.map((row) => (
          <li key={row.id}>
            <Link to={`/relaties/lijsten/${row.id}`} className="vonk-card flex h-full items-start gap-3 p-3 hover:shadow-md">
              {row.starts_on ? (
                <span className="flex w-12 shrink-0 flex-col items-center rounded-lg bg-muted py-1 leading-tight" aria-hidden>
                  <span className="text-xs font-semibold uppercase">{monthOf(row.starts_on)}</span>
                  <span className="text-lg font-semibold tabular-nums">{dayOfMonth(row.starts_on)}</span>
                  <span className="text-xs text-muted-foreground">{weekdayOf(row.starts_on)}</span>
                </span>
              ) : null}
              <span className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="truncate font-medium">{row.name}</span>
                {row.kind ? <CategoryChip category={row.kind} /> : null}
                <span className="truncate text-sm text-muted-foreground">{row.location ?? (row.type === 'dynamic' ? 'dynamisch' : '')}</span>
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                  {row.members_count === null ? null : <span className="tabular-nums">{row.members_count === 1 ? '1 persoon' : `${row.members_count} personen`}</span>}
                  {row.owner ? <EntityAvatar kind="person" name={row.owner.full_name} size="xs" /> : null}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}
